import { useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useProjects, useDeleteProject } from '../api/hooks'
import Button from '../components/ui/Button'
import SettingsForm from '../components/settings/SettingsForm'
import { getThumbnailUrl } from '../api/useAssetUrl'
import type { ProjectNodePreview } from '../api/types'

const MAX_CHIPS = 3

function PreviewChips({
  projectId,
  previews,
}: {
  projectId: string
  previews: ProjectNodePreview[]
}) {
  const [hidden, setHidden] = useState<Set<number>>(new Set())
  const onError = useCallback((idx: number) => {
    setHidden((prev) => new Set(prev).add(idx))
  }, [])

  const visible = previews.filter((_, i) => !hidden.has(i))
  if (visible.length === 0) return null

  const shown = visible.slice(0, MAX_CHIPS)

  return (
    <div className="hidden md:flex items-center gap-2 flex-shrink-0" data-testid="preview-chips">
      {shown.map((p) => {
        const url = getThumbnailUrl(projectId, p.image_path, 240)
        if (!url) return null
        return (
          <img
            key={p.image_path}
            src={url}
            alt=""
            onError={() => onError(previews.indexOf(p))}
            className="w-[120px] h-[80px] rounded-lg object-cover border border-border"
          />
        )
      })}
    </div>
  )
}

export default function HomePage() {
  const { data, isLoading, error } = useProjects()
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; name: string } | null>(null)
  const deleteProject = useDeleteProject()

  const handleDelete = async () => {
    if (!deleteConfirm) return
    try {
      await deleteProject.mutateAsync(deleteConfirm.id)
      setDeleteConfirm(null)
    } catch (err) {
      console.error('Failed to delete project:', err)
    }
  }

  if (isLoading) {
    return (
      <div data-page="landing" className="relative min-h-screen">
        <div className="max-w-5xl mx-auto px-6 py-8">
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="skeleton h-28 rounded-md bg-surface-1" />
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div data-page="landing" className="relative min-h-screen">
        <div className="max-w-3xl mx-auto px-6 py-8">
          <div className="bg-panel border border-border rounded-md p-8 animate-fade-in">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-lg bg-gradient-to-br from-yellow-500/20 to-orange-500/20 border border-yellow-500/20 mb-4">
                <svg className="w-8 h-8 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Closed Beta</h2>
              <p className="text-text-secondary">
                DreamWright Console is currently in closed testing.
              </p>
            </div>

            <div className="bg-black/30 rounded-md p-6 mb-6 border border-white/10">
              <p className="text-text-secondary mb-4 text-center text-sm">
                Access is limited to users with repository access.
              </p>
              <p className="text-text-muted text-sm mb-3">
                If you have access, start the API server:
              </p>
              <pre className="bg-black/40 rounded-lg p-4 text-sm overflow-x-auto font-mono">
                <code className="text-emerald-400">git clone</code> <code className="text-white/70">https://github.com/SFD-Hackathon/dreamwright.git</code>{'\n'}
                <code className="text-emerald-400">cd</code> <code className="text-white/70">dreamwright</code>{'\n'}
                <code className="text-emerald-400">uv run</code> <code className="text-white/70">dreamwright-api</code>
              </pre>
            </div>

            <div className="bg-black/30 rounded-md p-6 border border-white/10">
              <h3 className="font-semibold mb-4 text-center text-foreground">Configure Connection</h3>
              <SettingsForm compact />
            </div>
          </div>
        </div>
      </div>
    )
  }

  const projects = data?.data ?? []
  const userProjects = projects.filter((p) => !p.is_demo)

  return (
    <div data-page="landing" className="relative min-h-screen">
      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Delete Confirmation Modal */}
        {deleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm animate-fade-in">
            <div className="bg-panel rounded-lg p-6 max-w-md mx-4 shadow-2xl border border-border animate-scale-in">
              <div className="flex items-start gap-4 mb-6">
                <div className="p-3 bg-red-500/10 rounded-md border border-red-500/20">
                  <svg className="w-6 h-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-white">Delete Project?</h3>
                  <p className="text-text-secondary mt-2 text-sm">
                    Are you sure you want to delete &ldquo;<span className="text-white/70">{deleteConfirm.name}</span>&rdquo;? This action cannot be undone.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3">
                <Button
                  variant="secondary"
                  onClick={() => setDeleteConfirm(null)}
                  disabled={deleteProject.isPending}
                >
                  Cancel
                </Button>
                <Button
                  variant="default"
                  className="!bg-red-600 hover:!bg-red-700 !shadow !shadow-red-600/20"
                  onClick={handleDelete}
                  loading={deleteProject.isPending}
                >
                  Delete
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* User Projects */}
        {userProjects.length > 0 && (
          <section className="animate-slide-up" style={{ animationDelay: '0.1s' }}>
            <div className="flex items-center gap-3 mb-5">
              <h2 className="text-lg font-semibold text-white">Your Projects</h2>
              <span className="px-2.5 py-0.5 text-xs font-medium text-text-secondary bg-surface-2 border border-white/10 rounded-full">
                {userProjects.length}
              </span>
            </div>
            <div className="space-y-3">
              {userProjects.map((project) => (
                <div 
                  key={project.id} 
                  className="group p-5 rounded-md bg-panel border border-border hover:border-primary/50 transition-all duration-200"
                >
                  <div className="flex items-center gap-4">
                    <Link to={`/v2/project/${project.id}/workspace`} className="flex-1 min-w-0 cursor-pointer">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="px-2 py-0.5 text-xxs font-medium text-text-secondary bg-surface-2 border border-white/10 rounded uppercase tracking-wider">
                          {project.format}
                        </span>
                        <span className="px-2 py-0.5 text-xxs font-medium text-text-secondary bg-surface-2 border border-white/10 rounded uppercase tracking-wider">
                          {project.status}
                        </span>
                      </div>
                      <h3 className="text-lg font-semibold text-white group-hover:text-primary transition-colors mb-3 truncate">
                        {project.name}
                      </h3>
                      <div className="flex gap-3 flex-wrap">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-black/30 rounded-lg text-xs text-text-secondary">
                          <span className="font-semibold text-foreground">{project.chapter_count}</span> chapters
                        </span>
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-black/30 rounded-lg text-xs text-text-secondary">
                          <span className="font-semibold text-foreground">{project.character_count}</span> characters
                        </span>
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-black/30 rounded-lg text-xs text-text-secondary">
                          <span className="font-semibold text-foreground">{project.location_count}</span> locations
                        </span>
                      </div>
                      <div className="flex gap-3 flex-wrap mt-2">
                        <span className="text-xs text-text-muted">
                          Created {new Date(project.created_at).toLocaleString()}
                        </span>
                        <span className="text-xs text-text-muted">
                          Modified {new Date(project.updated_at).toLocaleString()}
                        </span>
                      </div>
                    </Link>
                    {(() => {
                      const allPreviews = [
                        ...(project.character_previews ?? []),
                        ...(project.location_previews ?? []),
                      ].filter((p) => p.image_path)
                      return allPreviews.length > 0 ? (
                        <PreviewChips projectId={project.id} previews={allPreviews} />
                      ) : null
                    })()}
                    <button
                      onClick={(e) => {
                        e.preventDefault()
                        e.stopPropagation()
                        setDeleteConfirm({ id: project.id, name: project.name })
                      }}
                      className="p-2.5 hover:bg-red-500/10 border border-transparent hover:border-red-500/20 rounded-lg transition-all cursor-pointer flex-shrink-0"
                      title="Delete Project"
                    >
                      <svg className="w-4 h-4 text-white/30 group-hover:text-red-400 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
        
        {/* Empty state */}
        {userProjects.length === 0 && (
          <div className="text-center py-16 animate-fade-in">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-lg bg-panel border border-border mb-6">
              <svg className="w-8 h-8 text-white/30" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-white mb-2">No projects yet</h3>
            <p className="text-text-secondary text-sm">Projects created via the agent workspace will appear here.</p>
          </div>
        )}
      </div>
    </div>
  )
}
